import openai from './openaiClient';

/**
 * Generates AI content for press releases, social media, and other PR materials
 */
export class AIContentGenerator {
  
  /**
   * Generates a press release based on user input
   * @param {Object} params - Press release parameters
   * @returns {Promise<Object>} Generated press release content
   */
  static async generatePressRelease(params) {
    try {
      const { topic, company, industry, keyPoints, tone = 'professional' } = params;
      
      const prompt = `Create a professional press release for ${company} in the ${industry} industry about ${topic}. 
      Key points to include: ${keyPoints?.join(', ')}. 
      Use a ${tone} tone and follow AP style guidelines.`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o',
        messages: [
          { 
            role: 'system', 
            content: 'You are an expert PR writer specializing in creating compelling press releases that follow journalistic standards and AP style guidelines.' 
          },
          { role: 'user', content: prompt },
        ],
        temperature: 0.7,
        max_tokens: 1500,
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'press_release',
            schema: {
              type: 'object',
              properties: {
                headline: { type: 'string' },
                subheadline: { type: 'string' },
                body: { type: 'string' },
                boilerplate: { type: 'string' },
                contact_info: { type: 'string' },
                seo_keywords: { type: 'array', items: { type: 'string' } }
              },
              required: ['headline', 'body', 'boilerplate'],
              additionalProperties: false,
            },
          },
        },
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (error) {
      console.error('Error generating press release:', error);
      throw error;
    }
  }

  /**
   * Generates social media content
   * @param {Object} params - Social media parameters
   * @returns {Promise<Object>} Generated social media content
   */
  static async generateSocialMediaContent(params) {
    try {
      const { platform, topic, tone = 'engaging', hashtags = [] } = params;
      
      const prompt = `Create ${platform} social media content about ${topic}. 
      Use a ${tone} tone and include relevant hashtags: ${hashtags?.join(' ')}. 
      Follow ${platform} best practices for engagement.`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini',
        messages: [
          { 
            role: 'system', 
            content: `You are a social media expert who creates engaging content for ${platform} that drives engagement and reaches target audiences.` 
          },
          { role: 'user', content: prompt },
        ],
        temperature: 0.8,
        max_tokens: 800,
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'social_media_content',
            schema: {
              type: 'object',
              properties: {
                main_post: { type: 'string' },
                caption: { type: 'string' },
                hashtags: { type: 'array', items: { type: 'string' } },
                call_to_action: { type: 'string' },
                best_posting_time: { type: 'string' }
              },
              required: ['main_post', 'hashtags'],
              additionalProperties: false,
            },
          },
        },
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (error) {
      console.error('Error generating social media content:', error);
      throw error;
    }
  }

  /**
   * Generates blog post content
   * @param {Object} params - Blog post parameters
   * @returns {Promise<Object>} Generated blog post content
   */
  static async generateBlogPost(params) {
    try {
      const { topic, targetAudience, wordCount = 800, keywords = [] } = params;
      
      const prompt = `Write a ${wordCount}-word blog post about ${topic} for ${targetAudience}. 
      Include SEO-optimized content with keywords: ${keywords?.join(', ')}. 
      Structure with clear headings, engaging introduction, and strong conclusion.`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o',
        messages: [
          { 
            role: 'system', 
            content: 'You are an expert content writer who creates SEO-optimized blog posts that engage readers and drive traffic.' 
          },
          { role: 'user', content: prompt },
        ],
        temperature: 0.7,
        max_tokens: 2000,
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'blog_post',
            schema: {
              type: 'object',
              properties: {
                title: { type: 'string' },
                meta_description: { type: 'string' },
                introduction: { type: 'string' },
                body_sections: { 
                  type: 'array', 
                  items: {
                    type: 'object',
                    properties: {
                      heading: { type: 'string' },
                      content: { type: 'string' }
                    },
                    required: ['heading', 'content']
                  }
                },
                conclusion: { type: 'string' },
                seo_keywords: { type: 'array', items: { type: 'string' } }
              },
              required: ['title', 'introduction', 'body_sections', 'conclusion'],
              additionalProperties: false,
            },
          },
        },
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (error) {
      console.error('Error generating blog post:', error);
      throw error;
    }
  }

  /**
   * Generates story angles and pitches
   * @param {Object} params - Story angle parameters
   * @returns {Promise<Object>} Generated story angles
   */
  static async generateStoryAngles(params) {
    try {
      const { topic, industry, newsHook, targetMedia = [] } = params;
      
      const prompt = `Generate 5 unique story angles for ${topic} in the ${industry} industry. 
      News hook: ${newsHook}. Target media types: ${targetMedia?.join(', ')}. 
      Each angle should be compelling and newsworthy with different approaches.`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o',
        messages: [
          { 
            role: 'system', 
            content: 'You are a PR strategist who creates compelling story angles that journalists want to cover. Focus on newsworthiness, human interest, and unique perspectives.' 
          },
          { role: 'user', content: prompt },
        ],
        temperature: 0.8,
        max_tokens: 1500,
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'story_angles',
            schema: {
              type: 'object',
              properties: {
                angles: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      angle_title: { type: 'string' },
                      description: { type: 'string' },
                      target_media: { type: 'string' },
                      newsworthiness_score: { type: 'number' },
                      pitch_approach: { type: 'string' }
                    },
                    required: ['angle_title', 'description', 'pitch_approach']
                  }
                },
                recommended_timing: { type: 'string' },
                additional_resources: { type: 'array', items: { type: 'string' } }
              },
              required: ['angles'],
              additionalProperties: false,
            },
          },
        },
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (error) {
      console.error('Error generating story angles:', error);
      throw error;
    }
  }

  /**
   * Generates press kit content suggestions and headlines
   * @param {Object} params - Press kit parameters
   * @returns {Promise<Object>} Generated press kit content
   */
  static async generatePressKitContent(params) {
    try {
      const { bookTitle, authorName, genre, keyThemes, publisher, targetAudience } = params;
      
      const prompt = `Create comprehensive press kit content for the book "${bookTitle}" by ${authorName}. 
      Genre: ${genre}. Key themes: ${keyThemes?.join(', ')}. Published by: ${publisher}. 
      Target audience: ${targetAudience}. Include compelling headlines, author bio highlights, 
      book description, and key selling points for media coverage.`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o',
        messages: [
          { 
            role: 'system', 
            content: 'You are a publishing industry expert who creates compelling press kit content that generates media interest and drives book sales.' 
          },
          { role: 'user', content: prompt },
        ],
        temperature: 0.7,
        max_tokens: 2000,
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'press_kit_content',
            schema: {
              type: 'object',
              properties: {
                compelling_headlines: { type: 'array', items: { type: 'string' } },
                book_description: { type: 'string' },
                author_bio_highlights: { type: 'array', items: { type: 'string' } },
                key_selling_points: { type: 'array', items: { type: 'string' } },
                media_hooks: { type: 'array', items: { type: 'string' } },
                target_media_outlets: { type: 'array', items: { type: 'string' } },
                interview_talking_points: { type: 'array', items: { type: 'string' } },
                promotional_taglines: { type: 'array', items: { type: 'string' } }
              },
              required: ['compelling_headlines', 'book_description', 'key_selling_points'],
              additionalProperties: false,
            },
          },
        },
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (error) {
      console.error('Error generating press kit content:', error);
      throw error;
    }
  }

  /**
   * Optimizes existing content for better performance
   * @param {Object} params - Content optimization parameters
   * @returns {Promise<Object>} Optimized content suggestions
   */
  static async optimizeContent(params) {
    try {
      const { content, contentType, targetGoal } = params;
      
      const prompt = `Analyze and optimize this ${contentType} content for ${targetGoal}. 
      Provide specific improvements for headlines, structure, SEO, and engagement.
      
      Original content: ${content}`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o',
        messages: [
          { 
            role: 'system', 
            content: 'You are a content optimization expert who analyzes existing content and provides actionable improvements for better performance and engagement.' 
          },
          { role: 'user', content: prompt },
        ],
        temperature: 0.6,
        max_tokens: 1500,
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'content_optimization',
            schema: {
              type: 'object',
              properties: {
                optimized_headline: { type: 'string' },
                content_improvements: { type: 'array', items: { type: 'string' } },
                seo_recommendations: { type: 'array', items: { type: 'string' } },
                engagement_tips: { type: 'array', items: { type: 'string' } },
                performance_score: { type: 'number' },
                revised_content: { type: 'string' }
              },
              required: ['optimized_headline', 'content_improvements', 'revised_content'],
              additionalProperties: false,
            },
          },
        },
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (error) {
      console.error('Error optimizing content:', error);
      throw error;
    }
  }
}

export default AIContentGenerator;